import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'
import '../styles/viewer.css' // reused for .custom-color-picker
import {
    getAreas,
    getTopics,
    getIndicators,
    getCountries,
} from '../actions/searchActions'
import {
    getFolders,
    getSeries,
} from '../actions/platformActions'
import { buildOrderedIndicatorGroups, getUnitsInOrder, sortUnitsForDisplay } from '../utils/seriesOptions'
import YearRangeSlider from './YearRangeSlider'
import { getTransformationLabel } from '../utils/transformations'
import { suggestSecondaryAxis } from '../utils/seriesChartOptions'

const DEFAULT_COLOR = '#385684'
const COUNTRY_COLOR_PALETTE = ['#385684', '#0C6BB2', '#2AAC9C', '#D68C2C', '#7A5BA6', '#B84A62', '#5F7E3E', '#6B778D']
const DEFAULT_CARD_BACKGROUND_COLOR = '#ffffff'
const DASHBOARD_SELECT_STYLES = {
    menuPortal: base => ({ ...base, zIndex: 2000 }),
}

const DASHBOARD_SELECT_PROPS = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: DASHBOARD_SELECT_STYLES,
}

const NO_TRANSFORMATION_OPTION = { value: '', label: 'None' }

const normalizeTransformation = (value) => String(value || '').trim()
const getTransformationOption = value => {
    const normalized = normalizeTransformation(value?.value ?? value)
    return normalized
        ? { value: normalized, label: getTransformationLabel(normalized) }
        : NO_TRANSFORMATION_OPTION
}

const filterRowsByMetric = (rows, unit, transformation = '') => {
    const requestedTransformation = normalizeTransformation(transformation)
    return (rows || []).filter(row => (
        row.unit === unit &&
        normalizeTransformation(row.transformation) === requestedTransformation
    ))
}

function buildCountryPayloadFromMeta(meta) {
    const countries = meta?.chart?.country || []
    const sourceCountries = countries.length > 0
        ? countries
        : (meta?.countries_used || []).map(country => ({ value: country, label: country }))

    return sourceCountries
        .filter(item => item?.value || item?.label)
        .map((item, index) => ({
            country: item.value || item.label,
            color: item.color || meta?.chart?.colors?.[index] || DEFAULT_COLOR,
            type: item.type || 'line',
            countryType: item.countryType || 'country',
        }))
}

function buildChartCountryFromPayload(countryPayload) {
    return countryPayload.map((item, index) => ({
        id: index,
        value: item.country,
        label: item.country,
        color: item.color || DEFAULT_COLOR,
        type: item.type || 'line',
        countryType: item.countryType || 'country',
    }))
}

// Same formula SeriesWidget uses to auto-generate a subtitle when there's no
// custom one saved — reused here so the editor opens with that text already
// in the field instead of blank, matching what's actually shown on the card.
function computeAutoSubtitle(source) {
    const indicator = source?.indicator || source?.meta_data?.indicator || ''
    if (!indicator) return ''
    const frequency = source?.meta_data?.chart?.frequency?.value || source?.frequency || 'Annual'
    return indicator + (frequency ? ` (${frequency})` : '')
}

function EditWidgetSeries({ closeCallback, updateCallback, wid, data: currentData = {} }) {
    const dispatch = useDispatch()
    const hasExistingSeries = Object.keys(currentData || {}).length > 0
    const initializedFromExistingSeries = useRef(false)

    const [mode, setMode] = useState('search')
    const [countryValue, setCountryValue] = useState([])
    const [indicatorValue, setIndicatorValue] = useState(null)
    const [frequencyValue, setFrequencyValue] = useState(null)
    const [unitValue, setUnitValue] = useState(null)
    const [transformationValue, setTransformationValue] = useState(
        getTransformationOption(currentData.meta_data?.chart?.transformation)
    )
    const [titleValue, setTitleValue] = useState(currentData.series_name || '')
    const [subtitleValue, setSubtitleValue] = useState(
        currentData.meta_data?.chart?.customSubtitle || computeAutoSubtitle(currentData)
    )
    const [backgroundColorValue, setBackgroundColorValue] = useState(currentData.backgroundColor || '#ffffff')
    const [frequencyOptions, setFrequencyOptions] = useState([])
    const [unitOptions, setUnitOptions] = useState([])
    const [transformationOptions, setTransformationOptions] = useState([NO_TRANSFORMATION_OPTION])
    const [loadedSeriesItem, setLoadedSeriesItem] = useState(null)
    const [availableIndicatorIds, setAvailableIndicatorIds] = useState(null)
    const [loadingAvailability, setLoadingAvailability] = useState(false)
    const [loadingSeriesOptions, setLoadingSeriesOptions] = useState(false)
    const [loadingAdd, setLoadingAdd] = useState(false)
    const [modalError, setModalError] = useState('')
    const [queryChanged, setQueryChanged] = useState(false)

    // Once a series is actually loaded (existing widget being edited, or a
    // freshly-picked one), let the user trim it to a year range — same
    // slider used in Search/Viewer, wired to the exact rows that will be
    // saved (filtered by the selected unit + variation, not the raw
    // multi-metric series).
    // Pre-populate from the widget's saved display window, if any, so
    // reopening an already-narrowed widget shows what it currently displays
    // instead of silently resetting to the full range. seriesAvailableYears
    // (below) still reflects the TRUE full range from source, so the user
    // can always widen back out from here.
    const [seriesFromYear, setSeriesFromYear] = useState(currentData.meta_data?.chart?.fromYear || '')
    const [seriesToYear, setSeriesToYear] = useState(currentData.meta_data?.chart?.toYear || '')

    const [folderValue, setFolderValue] = useState(null)
    const [seriesValue, setSerieValue] = useState(null)
    const [folderOptions, setFolderOptions] = useState([])
    const [seriesOptions, setSeriesOptions] = useState([])

    // Comparison series ("Series 2") — mirrors the Series 1 state above, kept
    // under new, optional keys (`series2`/`series2Data`/`useSecondaryAxis`)
    // so a widget saved before this existed never carries any of this.
    const [comparisonMode, setComparisonMode] = useState(Boolean(currentData.series2))
    const initializedFromExistingSeries2 = useRef(false)
    const [countryValue2, setCountryValue2] = useState([])
    const [indicatorValue2, setIndicatorValue2] = useState(null)
    const [frequencyValue2, setFrequencyValue2] = useState(null)
    const [unitValue2, setUnitValue2] = useState(null)
    const [transformationValue2, setTransformationValue2] = useState(NO_TRANSFORMATION_OPTION)
    const [frequencyOptions2, setFrequencyOptions2] = useState([])
    const [unitOptions2, setUnitOptions2] = useState([])
    const [transformationOptions2, setTransformationOptions2] = useState([NO_TRANSFORMATION_OPTION])
    const [loadedSeriesItem2, setLoadedSeriesItem2] = useState(null)
    const [loadingSeriesOptions2, setLoadingSeriesOptions2] = useState(false)
    const [modalError2, setModalError2] = useState('')
    // Auto-suggested once both series are loaded, but a user's own click is
    // never overwritten by a later re-suggestion.
    const [useSecondaryAxisValue, setUseSecondaryAxisValue] = useState(Boolean(currentData.meta_data?.chart?.useSecondaryAxis))
    const [secondaryAxisTouched, setSecondaryAxisTouched] = useState(false)

    useEffect(() => {
        dispatch(getFolders())
        dispatch(getAreas())
        dispatch(getTopics())
        dispatch(getIndicators())
        dispatch(getCountries())
    }, [])

    const { folderList } = useSelector(state => state.listFolder)
    const { listSeries } = useSelector(state => state.seriesList)
    const { areaList = [] } = useSelector(state => state.areaList)
    const { topicList = [] } = useSelector(state => state.topicList)
    const { loading: loadingIndicator, indicatorList = [] } = useSelector(state => state.indicatorList)
    const { loading: loadingCountry, countryList = [] } = useSelector(state => state.countryList)
    const [loadingFavoriteAdd, setLoadingFavoriteAdd] = useState(false)

    const countryOptions = useMemo(() => (
        countryList.map(item => ({
            value: item.id,
            label: item.name,
        }))
    ), [countryList])

    const indicatorOptions = useMemo(() => (
        buildOrderedIndicatorGroups(indicatorList, topicList, areaList, availableIndicatorIds)
    ), [areaList, availableIndicatorIds, indicatorList, topicList])

    useEffect(() => {
        if (folderList) {
            setFolderOptions(folderList.map(item => ({
                value: item.id,
                label: item.folder_name,
            })))
        }
    }, [folderList])

    useEffect(() => {
        if (listSeries) {
            setSeriesOptions(listSeries.map(item => ({
                value: item.id,
                label: item.series_name,
                raw: item,
            })))
        }
    }, [listSeries])

    useEffect(() => {
        if (!hasExistingSeries || initializedFromExistingSeries.current) return

        const countryNames = currentData.country_list?.length
            ? currentData.country_list.map(item => item.country).filter(Boolean)
            : (currentData.meta_data?.chart?.country || []).map(item => item.value || item.label).filter(Boolean)
        const frequency = currentData.meta_data?.chart?.frequency?.value || currentData.frequency || currentData.meta_data?.frequency || 'Annual'
        const unit = currentData.meta_data?.chart?.unit?.value || currentData.unit || ''
        const transformation = currentData.meta_data?.chart?.transformation
        const indicatorName = currentData.meta_data?.indicator || currentData.indicator || ''
        const topicName = currentData.meta_data?.topic || ''

        if (countryNames.length > 0) {
            const countries = countryNames.map(countryName => (
                countryOptions.find(item => item.label === countryName) || { value: countryName, label: countryName }
            ))
            setCountryValue(countries)
        }

        if (indicatorName && !indicatorValue) {
            const topic = topicList.find(item => item.name === topicName)
            const indicator = indicatorList.find(item => item.name === indicatorName && (!topic || item.foreign_topic === topic.id))
            setIndicatorValue({
                value: indicator?.id || indicatorName,
                label: indicatorName,
                area: indicator?.foreign_area,
                topic: indicator?.foreign_topic || topic?.id,
                topicApi: topicName,
                api: indicatorName,
            })
        }

        if (frequency && !frequencyValue) {
            setFrequencyValue({ value: frequency, label: frequency })
        }

        if (unit && !unitValue) {
            setUnitValue({ value: unit, label: unit })
        }
        if (transformation && transformation.value !== undefined) {
            setTransformationValue(getTransformationOption(transformation))
        }
        initializedFromExistingSeries.current = true
    }, [countryOptions, currentData, frequencyValue, hasExistingSeries, indicatorList, indicatorValue, topicList, unitValue])

    useEffect(() => {
        if (!hasExistingSeries || initializedFromExistingSeries2.current) return
        const series2 = currentData.series2
        if (!series2) {
            initializedFromExistingSeries2.current = true
            return
        }

        const countryNames2 = (series2.country_list || []).map(item => item.country).filter(Boolean)
        if (countryNames2.length > 0) {
            const countries = countryNames2.map(countryName => (
                countryOptions.find(item => item.label === countryName) || { value: countryName, label: countryName }
            ))
            setCountryValue2(countries)
        }

        if (series2.indicator && !indicatorValue2) {
            const indicator = indicatorList.find(item => item.name === series2.indicator)
            const topic = topicList.find(item => item.id === indicator?.foreign_topic)
            setIndicatorValue2({
                value: indicator?.id || series2.indicator,
                label: series2.indicator,
                area: indicator?.foreign_area,
                topic: indicator?.foreign_topic,
                topicApi: topic?.name || '',
                api: series2.indicator,
            })
        }

        if (series2.frequency && !frequencyValue2) setFrequencyValue2({ value: series2.frequency, label: series2.frequency })
        if (series2.unit && !unitValue2) setUnitValue2({ value: series2.unit, label: series2.unit })
        if (series2.transformation) setTransformationValue2(getTransformationOption(series2.transformation))
        initializedFromExistingSeries2.current = true
    }, [countryOptions, currentData.series2, frequencyValue2, hasExistingSeries, indicatorList, indicatorValue2, topicList, unitValue2])

    // No country-availability filtering for the comparison indicator picker
    // (unlike Series 1's `indicatorOptions`) — Series 2 is deliberately meant
    // to pick a DIFFERENT country/indicator than Series 1, so filtering its
    // list down to what Series 1's countries have would be the wrong signal.
    const indicatorOptions2 = useMemo(() => (
        buildOrderedIndicatorGroups(indicatorList, topicList, areaList, null)
    ), [areaList, indicatorList, topicList])

    useEffect(() => {
        const fetchSeriesOptions2 = async () => {
            if (!comparisonMode) return
            setLoadedSeriesItem2(null)
            setModalError2('')

            if (!countryValue2 || countryValue2.length === 0 || !indicatorValue2) return

            setLoadingSeriesOptions2(true)
            try {
                const requestedFrequency2 = frequencyValue2?.value || 'Annual'
                const { data } = await axios.post('/api/v1/data/request/', {
                    topic: indicatorValue2.topicApi ? [indicatorValue2.topicApi] : [],
                    indicator: [indicatorValue2.api],
                    country: countryValue2.map(country => country.label),
                    frequency: requestedFrequency2,
                }, { timeout: 30000 })

                const item = data?.data_list?.[0]
                if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
                    setUnitOptions2([])
                    setTransformationOptions2([NO_TRANSFORMATION_OPTION])
                    setModalError2('No data was found for this country and indicator.')
                    return
                }

                setLoadedSeriesItem2(item)

                const availableFrequencies2 = item.group.available_frequencies?.length
                    ? item.group.available_frequencies
                    : [item.group.frequency || requestedFrequency2]
                const nextFrequencyOptions2 = availableFrequencies2.map(value => ({ value, label: value }))
                setFrequencyOptions2(nextFrequencyOptions2)
                if (!frequencyValue2 || !availableFrequencies2.includes(frequencyValue2.value)) {
                    const nextFrequency2 = availableFrequencies2.includes(requestedFrequency2) ? requestedFrequency2 : availableFrequencies2[0]
                    setFrequencyValue2({ value: nextFrequency2, label: nextFrequency2 })
                }

                const unitsFromData2 = getUnitsInOrder(item.data)
                const units2 = unitsFromData2.length > 0 ? unitsFromData2 : sortUnitsForDisplay(item.group.units)
                setUnitOptions2(units2.map(value => ({ value, label: value })))
                if (!unitValue2 || !units2.includes(unitValue2.value)) {
                    setUnitValue2(units2[0] ? { value: units2[0], label: units2[0] } : null)
                }
            } catch (error) {
                setModalError2(error.code === 'ECONNABORTED'
                    ? 'The comparison series options took too long to load. Try another indicator or try again.'
                    : 'Frequency and metric options could not be loaded for the comparison series.'
                )
            } finally {
                setLoadingSeriesOptions2(false)
            }
        }

        fetchSeriesOptions2()
    }, [comparisonMode, countryValue2, frequencyValue2?.value, indicatorValue2])

    useEffect(() => {
        if (!loadedSeriesItem2 || !unitValue2) {
            setTransformationOptions2([NO_TRANSFORMATION_OPTION])
            return
        }

        const transformations2 = [...new Set((loadedSeriesItem2.data || [])
            .filter(row => row.unit === unitValue2.value)
            .map(row => normalizeTransformation(row.transformation))
            .filter(Boolean))]
            .sort()

        const nextTransformationOptions2 = [
            NO_TRANSFORMATION_OPTION,
            ...transformations2.map(value => ({ value, label: getTransformationLabel(value) })),
        ]
        setTransformationOptions2(nextTransformationOptions2)

        const currentOption2 = nextTransformationOptions2.find(option => option.value === transformationValue2?.value)
        if (!currentOption2) setTransformationValue2(NO_TRANSFORMATION_OPTION)
    }, [loadedSeriesItem2, unitValue2, transformationValue2?.value])

    useEffect(() => {
        if (secondaryAxisTouched) return
        if (!loadedSeriesItem || !unitValue || !loadedSeriesItem2 || !unitValue2) return

        const primaryValues = filterRowsByMetric(loadedSeriesItem.data, unitValue.value, transformationValue?.value).map(row => row.value)
        const secondaryValues = filterRowsByMetric(loadedSeriesItem2.data, unitValue2.value, transformationValue2?.value).map(row => row.value)
        setUseSecondaryAxisValue(suggestSecondaryAxis(primaryValues, secondaryValues))
    }, [loadedSeriesItem, unitValue, transformationValue?.value, loadedSeriesItem2, unitValue2, transformationValue2?.value, secondaryAxisTouched])

    const removeComparisonHandler = () => {
        setComparisonMode(false)
        setCountryValue2([])
        setIndicatorValue2(null)
        setFrequencyValue2(null)
        setUnitValue2(null)
        setTransformationValue2(NO_TRANSFORMATION_OPTION)
        setFrequencyOptions2([])
        setUnitOptions2([])
        setTransformationOptions2([NO_TRANSFORMATION_OPTION])
        setLoadedSeriesItem2(null)
        setModalError2('')
        setUseSecondaryAxisValue(false)
        setSecondaryAxisTouched(false)
    }

    useEffect(() => {
        const fetchAvailability = async () => {
            if (hasExistingSeries && !queryChanged) return
            if (!hasExistingSeries) {
                setIndicatorValue(null)
                setFrequencyValue(null)
                setUnitValue(null)
                setTransformationValue(NO_TRANSFORMATION_OPTION)
                setFrequencyOptions([])
                setUnitOptions([])
                setTransformationOptions([NO_TRANSFORMATION_OPTION])
                setLoadedSeriesItem(null)
            }
            setAvailableIndicatorIds(null)
            setModalError('')

            if (!countryValue || countryValue.length === 0) return

            setLoadingAvailability(true)
            try {
                const { data } = await axios.post('/api/v1/data/indicators-for-countries/', {
                    countries: countryValue.map(country => country.label),
                })
                const selectedCount = countryValue.length
                setAvailableIndicatorIds(new Set((data.indicators || [])
                    .filter(item => selectedCount <= 1 ? item.has_any_data : item.has_all_data)
                    .map(item => item.indicator_id)))
            } catch (error) {
                setModalError('Available indicators could not be loaded for the selected countries.')
            } finally {
                setLoadingAvailability(false)
            }
        }

        fetchAvailability()
    }, [countryValue, hasExistingSeries, queryChanged])

    useEffect(() => {
        const fetchSeriesOptions = async () => {
            // For an existing, untouched series this still fetches once on
            // mount (skips only once loadedSeriesItem is already set) —
            // needed so the year-range picker knows the TRUE full range
            // from source, not just whatever's in currentData.data. That
            // matters because currentData.data itself might already be a
            // previously-narrowed save (the actual bug being fixed here:
            // saving used to physically delete rows outside the picked
            // range, so a widget trimmed before this fix has no way to
            // recover its full range without going back to source).
            if (hasExistingSeries && !queryChanged && loadedSeriesItem) return
            setLoadedSeriesItem(null)
            setModalError('')

            if (!countryValue || countryValue.length === 0 || !indicatorValue) return

            setLoadingSeriesOptions(true)
            try {
                const requestedFrequency = frequencyValue?.value || currentData.meta_data?.chart?.frequency?.value || currentData.frequency || 'Annual'
                const { data } = await axios.post('/api/v1/data/request/', {
                    topic: indicatorValue.topicApi ? [indicatorValue.topicApi] : [],
                    indicator: [indicatorValue.api],
                    country: countryValue.map(country => country.label),
                    frequency: requestedFrequency,
                }, { timeout: 30000 })

                const item = data?.data_list?.[0]
                if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
                    setUnitOptions([])
                    setTransformationOptions([NO_TRANSFORMATION_OPTION])
                    setModalError('No data was found for this country and indicator.')
                    return
                }

                setLoadedSeriesItem(item)

                const availableFrequencies = item.group.available_frequencies?.length
                    ? item.group.available_frequencies
                    : [item.group.frequency || requestedFrequency]
                const nextFrequencyOptions = availableFrequencies.map(value => ({ value, label: value }))
                setFrequencyOptions(nextFrequencyOptions)
                if (!frequencyValue || !availableFrequencies.includes(frequencyValue.value)) {
                    const nextFrequency = availableFrequencies.includes(requestedFrequency) ? requestedFrequency : availableFrequencies[0]
                    setFrequencyValue({ value: nextFrequency, label: nextFrequency })
                }

                const unitsFromData = getUnitsInOrder(item.data)
                const units = unitsFromData.length > 0
                    ? unitsFromData
                    : sortUnitsForDisplay(item.group.units)
                const nextUnitOptions = units.map(value => ({ value, label: value }))
                setUnitOptions(nextUnitOptions)
                if (!unitValue || !units.includes(unitValue.value)) {
                    const existingUnit = currentData.unit || currentData.meta_data?.chart?.unit?.value
                    const nextUnit = existingUnit && units.includes(existingUnit) ? existingUnit : units[0]
                    setUnitValue(nextUnit ? { value: nextUnit, label: nextUnit } : null)
                }

            } catch (error) {
                setModalError(error.code === 'ECONNABORTED'
                    ? 'The series options took too long to load. Try another indicator or try again.'
                    : 'Frequency and metric options could not be loaded.'
                )
            } finally {
                setLoadingSeriesOptions(false)
            }
        }

        fetchSeriesOptions()
    }, [countryValue, currentData, frequencyValue?.value, hasExistingSeries, indicatorValue, queryChanged])

    const seriesAvailableYears = useMemo(() => {
        let rows
        if (loadedSeriesItem && unitValue) {
            // A fresh fetch happened (new series picked, or some field was
            // changed) — filter it down to the exact unit/variation that
            // will actually be saved.
            rows = filterRowsByMetric(loadedSeriesItem.data, unitValue.value, transformationValue?.value)
        } else if (hasExistingSeries && Array.isArray(currentData.data)) {
            // Editing an existing, untouched widget: fetchSeriesOptions
            // deliberately skips re-fetching when nothing changed
            // (`if (hasExistingSeries && !queryChanged) return`), so
            // loadedSeriesItem never gets populated — fall back to the
            // widget's already-saved data so the slider still has years to
            // show instead of just not appearing.
            rows = currentData.data
        } else {
            return []
        }
        return Array.from(new Set(
            rows
                .map(row => parseInt(String(row.date).split('-')[0], 10))
                .filter(year => !Number.isNaN(year))
        )).sort((a, b) => a - b)
    }, [loadedSeriesItem, unitValue, transformationValue, hasExistingSeries, currentData.data])

    useEffect(() => {
        if (seriesAvailableYears.length === 0) return
        setSeriesFromYear(prev => (prev && seriesAvailableYears.includes(Number(prev))) ? prev : String(seriesAvailableYears[0]))
        setSeriesToYear(prev => (prev && seriesAvailableYears.includes(Number(prev))) ? prev : String(seriesAvailableYears[seriesAvailableYears.length - 1]))
    }, [seriesAvailableYears])

    // True for the brief window where a metric is picked but the year
    // range isn't resolved yet — either still fetching (loadingSeriesOptions,
    // the "pick a new series" path), or fetched/derived but the from/to
    // effect above hasn't run yet (the "editing an existing series" path,
    // where seriesAvailableYears comes from currentData.data synchronously
    // but from/to still need one render tick to populate). Update/Add stays
    // disabled through this window so a save can't fire with a stale or
    // undefined year range.
    const yearsPending = Boolean(unitValue) && (
        loadingSeriesOptions ||
        (seriesAvailableYears.length > 1 && (!seriesFromYear || !seriesToYear))
    )

    useEffect(() => {
        if (!loadedSeriesItem || !unitValue) {
            setTransformationOptions([NO_TRANSFORMATION_OPTION])
            return
        }

        const transformations = [...new Set((loadedSeriesItem.data || [])
            .filter(row => row.unit === unitValue.value)
            .map(row => normalizeTransformation(row.transformation))
            .filter(Boolean))]
            .sort()

        const nextTransformationOptions = [
            NO_TRANSFORMATION_OPTION,
            ...transformations.map(value => ({
                value,
                label: getTransformationLabel(value),
            })),
        ]
        setTransformationOptions(nextTransformationOptions)

        const requestedTransformation = currentData.meta_data?.chart?.transformation?.value || ''
        const currentTransformationOption = nextTransformationOptions.find(option => option.value === transformationValue?.value)
        if (!currentTransformationOption) {
            const nextTransformation = nextTransformationOptions.find(option => option.value === requestedTransformation) || NO_TRANSFORMATION_OPTION
            setTransformationValue(nextTransformation)
        } else if (currentTransformationOption.label !== transformationValue?.label) {
            setTransformationValue(currentTransformationOption)
        }
    }, [currentData.meta_data?.chart?.transformation?.value, loadedSeriesItem, transformationValue?.label, transformationValue?.value, unitValue])

    const selectFolderHandler = (option) => {
        setFolderValue(option)
        setSerieValue(null)
        dispatch(getSeries(option.value))
    }

    const selectSerieHandler = (option) => {
        setSerieValue(option)
        if (option?.raw?.series_name && !titleValue.trim()) {
            setTitleValue(option.raw.series_name)
        }
        if (!subtitleValue.trim()) {
            const favoriteSubtitle = option?.raw?.meta_data?.chart?.customSubtitle || computeAutoSubtitle(option?.raw)
            if (favoriteSubtitle) setSubtitleValue(favoriteSubtitle)
        }
    }

    const updateFromFavoritesHandler = async () => {
        if (!folderValue || !seriesValue || loadingFavoriteAdd) return

        const favorite = seriesValue.raw
        const meta = favorite?.meta_data
        if (!meta?.indicator) {
            setModalError('This favorite does not have enough metadata to build the series.')
            return
        }

        setLoadingFavoriteAdd(true)
        setModalError('')
        try {
            const countryPayload = buildCountryPayloadFromMeta(meta)
            const countryNames = countryPayload
                .filter(item => item.countryType !== 'region')
                .map(item => item.country)

            if (countryNames.length === 0) {
                setModalError('This favorite does not include countries that can be added to the dashboard.')
                return
            }

            const frequency = meta.chart?.frequency?.value || meta.frequency || 'Annual'
            const unit = meta.chart?.unit?.value || meta.units?.[0] || ''
            const transformation = meta.chart?.transformation?.value || ''

            const { data } = await axios.post('/api/v1/data/request/', {
                topic: meta.topic ? [meta.topic] : [],
                indicator: [meta.indicator],
                country: countryNames,
                frequency,
            }, { timeout: 30000 })

            const item = data?.data_list?.[0]
            if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
                setModalError('No data was found for this favorite series.')
                return
            }

            const orderedUnits = getUnitsInOrder(item.data)
            const selectedUnit = unit || orderedUnits[0] || sortUnitsForDisplay(item.group.units)[0] || ''
            const filteredData = selectedUnit ? filterRowsByMetric(item.data, selectedUnit, transformation) : item.data

            if (filteredData.length === 0) {
                setModalError('No data was found for the favorite metric and transformation.')
                return
            }

            const chartCountry = meta.chart?.country?.length
                ? meta.chart.country
                : buildChartCountryFromPayload(countryPayload)

            const jsonContent = {
                serie_id: favorite.id,
                backgroundColor: backgroundColorValue,
                series_name: titleValue.trim() || favorite.series_name || `${item.group.indicator} - ${countryNames.join(', ')}`,
                indicator: item.group.indicator,
                frequency,
                country_list: countryPayload,
                unit: selectedUnit,
                transformation,
                xaxis: meta.chart?.xaxis || [],
                yaxis: meta.chart?.yaxis || [],
                highlight: meta.chart?.highlight || [],
                data: filteredData,
                meta_data: {
                    ...item.group,
                    ...meta,
                    chart: {
                        ...meta.chart,
                        unit: { value: selectedUnit, label: selectedUnit },
                        transformation: {
                            value: transformation,
                            label: transformation ? getTransformationLabel(transformation) : 'None',
                        },
                        frequency: { value: frequency, label: frequency },
                        country: chartCountry,
                        xaxis: meta.chart?.xaxis || [],
                        yaxis: meta.chart?.yaxis || [],
                        highlight: meta.chart?.highlight || [],
                        customSubtitle: subtitleValue.trim(),
                    },
                },
                folder_id: favorite.folders || favorite.folder_id || folderValue.value,
                folder_name: folderValue.label,
            }

            updateCallback(wid, jsonContent)
            closeCallback(true)
        } catch (error) {
            setModalError(error.code === 'ECONNABORTED'
                ? 'The favorite series request took too long. Try again or choose another favorite.'
                : 'The favorite series could not be loaded. Try again or choose another favorite.'
            )
        } finally {
            setLoadingFavoriteAdd(false)
        }
    }

    const directUpdateHandler = async () => {
        if (!countryValue || countryValue.length === 0 || !indicatorValue || !frequencyValue || !unitValue || !transformationValue || loadingAdd || loadingSeriesOptions) return
        if (comparisonMode && (!countryValue2 || countryValue2.length === 0 || !indicatorValue2 || !frequencyValue2 || !unitValue2 || !transformationValue2 || loadingSeriesOptions2)) return

        const selectedCountryNames = countryValue.map(country => country.label).filter(Boolean)
        const existingCountryNames = currentData.country_list?.length
            ? currentData.country_list.map(item => item.country).filter(Boolean)
            : (currentData.meta_data?.chart?.country || []).map(item => item.value || item.label).filter(Boolean)
        const existingIndicator = currentData.meta_data?.indicator || currentData.indicator || ''
        const existingFrequency = currentData.meta_data?.chart?.frequency?.value || currentData.frequency || currentData.meta_data?.frequency || 'Annual'
        const existingUnit = currentData.meta_data?.chart?.unit?.value || currentData.unit || ''
        const existingTransformation = currentData.meta_data?.chart?.transformation?.value || currentData.transformation || ''
        const sameCountries = selectedCountryNames.length === existingCountryNames.length &&
            selectedCountryNames.every((country, index) => country === existingCountryNames[index])
        // The year slider doesn't change country/indicator/frequency/unit/
        // transformation, so without this the "nothing changed, skip the
        // refetch" shortcut below would silently ignore a narrowed year
        // range and keep saving the old display window. Compare against
        // the saved *display* window (meta_data.chart.fromYear/toYear), not
        // against currentData.data's own min/max — data always holds the
        // full fetched range now (see the save logic below), so its min/max
        // would just be the full range and never look "changed".
        const existingFromYear = currentData.meta_data?.chart?.fromYear || ''
        const existingToYear = currentData.meta_data?.chart?.toYear || ''
        const sameYearRange = (seriesFromYear || '') === existingFromYear && (seriesToYear || '') === existingToYear
        // Comparison mode always goes through the full fetch path below —
        // detecting "series 2 also unchanged" here isn't worth the extra
        // bookkeeping, and the shortcut only exists to skip refetching
        // series 1 when nothing about it changed.
        const sameSeriesQuery = hasExistingSeries &&
            !comparisonMode &&
            sameCountries &&
            sameYearRange &&
            (indicatorValue.api || indicatorValue.label) === existingIndicator &&
            frequencyValue.value === existingFrequency &&
            unitValue.value === existingUnit &&
            normalizeTransformation(transformationValue.value) === normalizeTransformation(existingTransformation)

        if (sameSeriesQuery) {
            updateCallback(wid, {
                ...currentData,
                // Explicit `undefined` (rather than just omitting the keys)
                // so a save made right after clicking "Remove comparison"
                // actually drops a previously-saved series2 instead of this
                // spread of `...currentData` silently keeping it.
                series2: undefined,
                series2Data: undefined,
                backgroundColor: backgroundColorValue,
                series_name: titleValue.trim() || currentData.series_name || existingIndicator,
                meta_data: {
                    ...currentData.meta_data,
                    chart: {
                        ...currentData.meta_data?.chart,
                        customSubtitle: subtitleValue.trim(),
                        useSecondaryAxis: undefined,
                    },
                },
            })
            closeCallback(true)
            return
        }

        setLoadingAdd(true)
        setModalError('')
        try {
            let item = loadedSeriesItem
            if (!item || item.group?.frequency !== frequencyValue.value) {
                const { data } = await axios.post('/api/v1/data/request/', {
                    topic: indicatorValue.topicApi ? [indicatorValue.topicApi] : [],
                    indicator: [indicatorValue.api],
                    country: countryValue.map(country => country.label),
                    frequency: frequencyValue.value,
                }, { timeout: 30000 })
                item = data?.data_list?.[0]
            }

            if (!item?.group || !Array.isArray(item.data) || item.data.length === 0) {
                setModalError('No data was found for this country, indicator, frequency, and metric.')
                return
            }

            const unit = unitValue.value
            const transformation = transformationValue.value || ''
            const filteredData = unit ? filterRowsByMetric(item.data, unit, transformation) : item.data
            if (filteredData.length === 0) {
                setModalError('No data was found for the selected metric and transformation.')
                return
            }

            // The year slider narrows what's *displayed*, not what's saved:
            // `data` below always keeps every year fetched. SeriesWidget
            // already reads meta_data.chart.fromYear/toYear and filters at
            // render time — reusing that instead of physically deleting
            // rows means re-opening this modal later can always offer the
            // full range again, instead of the narrowed range becoming a
            // permanent ceiling (this was the actual bug: saving a trimmed
            // `data` array made the trim irreversible).
            const fromYear = seriesFromYear || ''
            const toYear = seriesToYear || ''

            const countryNames = countryValue.map(country => country.label)
            const countriesWithData = new Set(filteredData.map(row => row.country))
            const missingCountries = countryNames.filter(country => !countriesWithData.has(country))
            if (missingCountries.length > 0) {
                setModalError(`No data was found for ${missingCountries.join(', ')} with the selected metric.`)
                return
            }

            const countryPayload = countryNames.map((countryName, index) => ({
                value: countryName,
                label: countryName,
                color: currentData.country_list?.find(item => item.country === countryName)?.color || COUNTRY_COLOR_PALETTE[index % COUNTRY_COLOR_PALETTE.length],
                type: currentData.country_list?.find(item => item.country === countryName)?.type || 'line',
                countryType: 'country',
            }))

            const jsonContent = {
                serie_id: null,
                backgroundColor: backgroundColorValue,
                series_name: titleValue.trim() || `${item.group.indicator} - ${countryNames.join(', ')}`,
                indicator: item.group.indicator,
                frequency: frequencyValue.value,
                country_list: countryPayload.map(item => ({
                    country: item.value,
                    color: item.color,
                    type: item.type,
                    countryType: item.countryType,
                })),
                unit,
                transformation,
                xaxis: [],
                yaxis: [],
                highlight: [],
                data: filteredData,
                meta_data: {
                    ...item.group,
                    chart: {
                        unit: { value: unit, label: unit },
                        transformation: transformationValue,
                        frequency: frequencyValue,
                        country: countryPayload,
                        xaxis: [],
                        yaxis: [],
                        highlight: [],
                        fromYear,
                        toYear,
                        customSubtitle: subtitleValue.trim(),
                    },
                },
            }

            if (comparisonMode) {
                let item2 = loadedSeriesItem2
                if (!item2 || item2.group?.frequency !== frequencyValue2.value) {
                    const { data } = await axios.post('/api/v1/data/request/', {
                        topic: indicatorValue2.topicApi ? [indicatorValue2.topicApi] : [],
                        indicator: [indicatorValue2.api],
                        country: countryValue2.map(country => country.label),
                        frequency: frequencyValue2.value,
                    }, { timeout: 30000 })
                    item2 = data?.data_list?.[0]
                }

                if (!item2?.group || !Array.isArray(item2.data) || item2.data.length === 0) {
                    setModalError2('No data was found for the comparison series.')
                    return
                }

                const unit2 = unitValue2.value
                const transformation2 = transformationValue2.value || ''
                const filteredData2 = unit2 ? filterRowsByMetric(item2.data, unit2, transformation2) : item2.data
                if (filteredData2.length === 0) {
                    setModalError2('No data was found for the comparison metric and transformation.')
                    return
                }

                const countryNames2 = countryValue2.map(country => country.label)
                const countryList2 = countryNames2.map((countryName, index) => ({
                    country: countryName,
                    color: COUNTRY_COLOR_PALETTE[(countryPayload.length + index) % COUNTRY_COLOR_PALETTE.length],
                    type: 'line',
                    countryType: 'country',
                }))

                jsonContent.series2 = {
                    indicator: item2.group.indicator,
                    frequency: frequencyValue2.value,
                    unit: unit2,
                    transformation: transformation2,
                    country_list: countryList2,
                }
                jsonContent.series2Data = filteredData2
                jsonContent.meta_data.chart.useSecondaryAxis = useSecondaryAxisValue
            }

            updateCallback(wid, jsonContent)
            closeCallback(true)
        } catch (error) {
            setModalError(error.code === 'ECONNABORTED'
                ? 'The series request took too long. Try another indicator or try again.'
                : 'The series could not be loaded. Try again or choose another indicator.'
            )
        } finally {
            setLoadingAdd(false)
        }
    }

    return (
        <div className='modal-container'>
            <div className={`modal-window dashboard-series-modal${comparisonMode ? ' dashboard-series-modal--comparison' : ''} px-5 pb-5 pt-4 d-flex flex-column align-items-center`}>
                <i onClick={() => closeCallback(false)} style={{ cursor: 'pointer' }} className="fa-solid fa-xmark text-secondary fs-4 ms-auto"></i>
                <h1 style={{ color: '#313A52' }} className='bold-title fs-1'>{hasExistingSeries ? 'Edit Series' : 'Add Series'}</h1>

                <p className='p-paragraph text-center'>
                    Select countries, an indicator, frequency, metric, and title for the dashboard chart.
                </p>

                <span className='p-label-2 w-100'>Chart Title:</span>
                <input
                    className='dashboard-series-title-input'
                    type='text'
                    value={titleValue}
                    onChange={event => setTitleValue(event.target.value)}
                    placeholder='Use the default title or write a custom one'
                />

                <span className='p-label-2 w-100'>Chart Subtitle:</span>
                <input
                    className='dashboard-series-title-input'
                    type='text'
                    value={subtitleValue}
                    onChange={event => setSubtitleValue(event.target.value)}
                    placeholder='Use the default subtitle (indicator and frequency) or write a custom one'
                />

                <div className='d-flex align-items-center w-100 my-2' style={{ gap: '0.5em' }}>
                    <span className='p-label-2'>Card background:</span>
                    <input value={backgroundColorValue} onChange={event => setBackgroundColorValue(event.target.value)} className='custom-color-picker' type='color' />
                    {backgroundColorValue !== DEFAULT_CARD_BACKGROUND_COLOR && (
                        <i
                            onClick={() => setBackgroundColorValue(DEFAULT_CARD_BACKGROUND_COLOR)}
                            style={{ cursor: 'pointer', color: '#808080' }}
                            className="fa-solid fa-arrow-rotate-left"
                            title='Reset to default background'
                        ></i>
                    )}
                </div>

                <div className='dashboard-series-tabs'>
                    <button type='button' className={mode === 'search' ? 'active' : ''} onClick={() => setMode('search')}>Search series</button>
                    <button type='button' className={mode === 'favorites' ? 'active' : ''} onClick={() => setMode('favorites')}>Favorites</button>
                </div>

                {mode === 'search' ? (
                    <>
                    <div className={`w-100 ${comparisonMode ? 'dashboard-series-comparison-columns' : ''}`}>
                    <div className='w-100'>
                        <span className='p-label-2 w-100'>Select Countries:</span>
                        <Select
                            className={`custom-select-container ${loadingCountry && 'button-disabled'}`}
                            classNamePrefix="search-select"
                            options={countryOptions}
                            placeholder='Select one or more countries'
                            value={countryValue}
                            isMulti
                            closeMenuOnSelect={false}
                            onMenuOpen={() => setQueryChanged(true)}
                            onChange={option => {
                                setQueryChanged(true)
                                setCountryValue(option || [])
                                setIndicatorValue(null)
                                setFrequencyValue(null)
                                setUnitValue(null)
                                setTransformationValue(NO_TRANSFORMATION_OPTION)
                                setFrequencyOptions([])
                                setUnitOptions([])
                                setTransformationOptions([NO_TRANSFORMATION_OPTION])
                                setLoadedSeriesItem(null)
                            }}
                            {...DASHBOARD_SELECT_PROPS}
                        />

                        {loadingAvailability && (
                            <div className='dashboard-series-helper'>Loading available indicators...</div>
                        )}

                        <span className='p-label-2 text-start w-100 pt-3'>Select Indicator:</span>
                        <Select
                            className={`custom-select-container ${((!countryValue || countryValue.length === 0) || loadingIndicator || loadingAvailability) && 'button-disabled'}`}
                            classNamePrefix="search-select"
                            options={indicatorOptions}
                            placeholder={countryValue.length > 0 ? 'Search available indicators' : 'Select countries first'}
                            value={indicatorValue}
                            onMenuOpen={() => setQueryChanged(true)}
                            onChange={option => {
                                setQueryChanged(true)
                                setIndicatorValue(option)
                                setFrequencyValue(null)
                                setUnitValue(null)
                                setTransformationValue(NO_TRANSFORMATION_OPTION)
                                setFrequencyOptions([])
                                setUnitOptions([])
                                setTransformationOptions([NO_TRANSFORMATION_OPTION])
                                setLoadedSeriesItem(null)
                            }}
                            {...DASHBOARD_SELECT_PROPS}
                        />

                        {loadingSeriesOptions && (
                            <div className='dashboard-series-helper'>Loading frequency and metric options...</div>
                        )}

                        <div className='dashboard-series-grid'>
                            <div>
                                <span className='p-label-2 text-start w-100 pt-3'>Select Frequency:</span>
                                <Select
                                    className={`custom-select-container ${(!indicatorValue || loadingSeriesOptions) && 'button-disabled'}`}
                                    classNamePrefix="search-select"
                                    options={frequencyOptions}
                                    placeholder='Select frequency'
                                    value={frequencyValue}
                                    onMenuOpen={() => setQueryChanged(true)}
                                    onChange={option => {
                                        setQueryChanged(true)
                                        setFrequencyValue(option)
                                        setUnitValue(null)
                                        setTransformationValue(NO_TRANSFORMATION_OPTION)
                                        setUnitOptions([])
                                        setTransformationOptions([NO_TRANSFORMATION_OPTION])
                                        setLoadedSeriesItem(null)
                                    }}
                                    {...DASHBOARD_SELECT_PROPS}
                                />
                            </div>

                            <div>
                                <span className='p-label-2 text-start w-100 pt-3'>Select Metric:</span>
                                <Select
                                    className={`custom-select-container ${(!frequencyValue || loadingSeriesOptions) && 'button-disabled'}`}
                                    classNamePrefix="search-select"
                                    options={unitOptions}
                                    placeholder='Select metric'
                                    value={unitValue}
                                    onMenuOpen={() => setQueryChanged(true)}
                                    onChange={option => {
                                        setQueryChanged(true)
                                        setUnitValue(option)
                                        setTransformationValue(NO_TRANSFORMATION_OPTION)
                                    }}
                                    {...DASHBOARD_SELECT_PROPS}
                                />
                            </div>
                        </div>

                        {transformationOptions.length > 1 && (
                            <div className='dashboard-series-grid'>
                                <div>
                                    <span className='p-label-2 text-start w-100 pt-3'>Select Variation:</span>
                                    <Select
                                        className={`custom-select-container ${(!unitValue || loadingSeriesOptions) && 'button-disabled'}`}
                                        classNamePrefix="search-select"
                                        options={transformationOptions}
                                        placeholder='Select variation'
                                        value={transformationValue}
                                        onMenuOpen={() => setQueryChanged(true)}
                                        onChange={option => {
                                            setQueryChanged(true)
                                            setTransformationValue(option || NO_TRANSFORMATION_OPTION)
                                        }}
                                        {...DASHBOARD_SELECT_PROPS}
                                    />
                                </div>
                            </div>
                        )}

                    </div>

                    {comparisonMode && (
                        <div className='dashboard-series-divider ps-4'>
                            <div className='dashboard-series-comparison-column-header'>
                                <h2>Compare with:</h2>
                                <button type='button' onClick={removeComparisonHandler}>Remove comparison</button>
                            </div>

                            <span className='p-label-2 w-100'>Select Countries:</span>
                            <Select
                                className='custom-select-container'
                                classNamePrefix="search-select"
                                options={countryOptions}
                                placeholder='Select one or more countries'
                                value={countryValue2}
                                isMulti
                                closeMenuOnSelect={false}
                                onChange={option => {
                                    setCountryValue2(option || [])
                                    setIndicatorValue2(null)
                                    setFrequencyValue2(null)
                                    setUnitValue2(null)
                                    setTransformationValue2(NO_TRANSFORMATION_OPTION)
                                    setFrequencyOptions2([])
                                    setUnitOptions2([])
                                    setTransformationOptions2([NO_TRANSFORMATION_OPTION])
                                    setLoadedSeriesItem2(null)
                                    setSecondaryAxisTouched(false)
                                }}
                                {...DASHBOARD_SELECT_PROPS}
                            />

                            <span className='p-label-2 text-start w-100 pt-3'>Select Indicator:</span>
                            <Select
                                className={`custom-select-container ${((!countryValue2 || countryValue2.length === 0) || loadingIndicator) && 'button-disabled'}`}
                                classNamePrefix="search-select"
                                options={indicatorOptions2}
                                placeholder={countryValue2.length > 0 ? 'Search available indicators' : 'Select countries first'}
                                value={indicatorValue2}
                                onChange={option => {
                                    setIndicatorValue2(option)
                                    setFrequencyValue2(null)
                                    setUnitValue2(null)
                                    setTransformationValue2(NO_TRANSFORMATION_OPTION)
                                    setFrequencyOptions2([])
                                    setUnitOptions2([])
                                    setTransformationOptions2([NO_TRANSFORMATION_OPTION])
                                    setLoadedSeriesItem2(null)
                                    setSecondaryAxisTouched(false)
                                }}
                                {...DASHBOARD_SELECT_PROPS}
                            />

                            {loadingSeriesOptions2 && (
                                <div className='dashboard-series-helper'>Loading frequency and metric options...</div>
                            )}

                            <div className='dashboard-series-grid'>
                                <div>
                                    <span className='p-label-2 text-start w-100 pt-3'>Select Frequency:</span>
                                    <Select
                                        className={`custom-select-container ${(!indicatorValue2 || loadingSeriesOptions2) && 'button-disabled'}`}
                                        classNamePrefix="search-select"
                                        options={frequencyOptions2}
                                        placeholder='Select frequency'
                                        value={frequencyValue2}
                                        onChange={option => {
                                            setFrequencyValue2(option)
                                            setUnitValue2(null)
                                            setTransformationValue2(NO_TRANSFORMATION_OPTION)
                                            setUnitOptions2([])
                                            setTransformationOptions2([NO_TRANSFORMATION_OPTION])
                                            setLoadedSeriesItem2(null)
                                            setSecondaryAxisTouched(false)
                                        }}
                                        {...DASHBOARD_SELECT_PROPS}
                                    />
                                </div>

                                <div>
                                    <span className='p-label-2 text-start w-100 pt-3'>Select Metric:</span>
                                    <Select
                                        className={`custom-select-container ${(!frequencyValue2 || loadingSeriesOptions2) && 'button-disabled'}`}
                                        classNamePrefix="search-select"
                                        options={unitOptions2}
                                        placeholder='Select metric'
                                        value={unitValue2}
                                        onChange={option => {
                                            setUnitValue2(option)
                                            setTransformationValue2(NO_TRANSFORMATION_OPTION)
                                            setSecondaryAxisTouched(false)
                                        }}
                                        {...DASHBOARD_SELECT_PROPS}
                                    />
                                </div>
                            </div>

                            {transformationOptions2.length > 1 && (
                                <div className='dashboard-series-grid'>
                                    <div>
                                        <span className='p-label-2 text-start w-100 pt-3'>Select Variation:</span>
                                        <Select
                                            className={`custom-select-container ${(!unitValue2 || loadingSeriesOptions2) && 'button-disabled'}`}
                                            classNamePrefix="search-select"
                                            options={transformationOptions2}
                                            placeholder='Select variation'
                                            value={transformationValue2}
                                            onChange={option => {
                                                setTransformationValue2(option || NO_TRANSFORMATION_OPTION)
                                                setSecondaryAxisTouched(false)
                                            }}
                                            {...DASHBOARD_SELECT_PROPS}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className='dashboard-series-range-row'>
                                <label>
                                    <input
                                        type='checkbox'
                                        checked={useSecondaryAxisValue}
                                        onChange={event => {
                                            setSecondaryAxisTouched(true)
                                            setUseSecondaryAxisValue(event.target.checked)
                                        }}
                                    />
                                    Use a separate Y-axis (right) for this series
                                </label>
                            </div>

                            {modalError2 && (
                                <div className='dashboard-series-warning'>{modalError2}</div>
                            )}
                        </div>
                    )}
                    </div>

                    {unitValue && yearsPending && (
                        <div className='dashboard-series-helper pt-3'>
                            <Spinner animation="border" as='span' style={{ width: '12px', height: '12px', borderWidth: '2px' }} className='me-2' />
                            Loading years...
                        </div>
                    )}

                    {!yearsPending && seriesAvailableYears.length > 1 && (
                        <div className='pt-3 w-100'>
                            <span className='p-label-2 text-start w-100'>Year range:</span>
                            <YearRangeSlider
                                years={seriesAvailableYears}
                                fromYear={seriesFromYear}
                                toYear={seriesToYear}
                                onChange={(from, to) => {
                                    setSeriesFromYear(String(from))
                                    setSeriesToYear(String(to))
                                }}
                            />
                        </div>
                    )}

                    {!comparisonMode && (
                        <button type='button' className='dashboard-series-comparison-toggle' onClick={() => setComparisonMode(true)}>
                            + Compare with another series
                        </button>
                    )}

                        {modalError && (
                            <div className='dashboard-series-warning'>{modalError}</div>
                        )}

                        <div onClick={directUpdateHandler} style={{ width: '100%' }} className={`login-button ${(!countryValue || countryValue.length === 0 || !indicatorValue || !frequencyValue || !unitValue || !transformationValue || loadingAdd || loadingSeriesOptions || yearsPending || (comparisonMode && (!countryValue2 || countryValue2.length === 0 || !indicatorValue2 || !frequencyValue2 || !unitValue2 || !transformationValue2 || loadingSeriesOptions2))) ? 'button-disabled' : ''} mt-3`}>
                            {loadingAdd ? (
                                <Spinner animation="border" variant='light' as='span' style={{ width: '20px', height: '20px' }} />
                            ) : (
                                hasExistingSeries ? 'Update Serie' : 'Add Serie'
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <span className='p-label-2 w-100'>Select a folder:</span>
                        <Select
                            className='custom-select-container'
                            classNamePrefix='custom-select'
                            placeholder='Select folder'
                            value={folderValue}
                            options={folderOptions}
                            onChange={option => selectFolderHandler(option)}
                            {...DASHBOARD_SELECT_PROPS}
                        />

                        <span className='p-label-2 text-start w-100 pt-4'>Select one of your favorites:</span>
                        <Select
                            className={`custom-select-container ${!folderValue ? 'button-disabled' : ''}`}
                            classNamePrefix='custom-select'
                            placeholder='Select series'
                            value={seriesValue}
                            options={seriesOptions}
                            onChange={option => selectSerieHandler(option)}
                            {...DASHBOARD_SELECT_PROPS}
                        />

                        {modalError && (
                            <div className='dashboard-series-warning'>{modalError}</div>
                        )}

                        <div onClick={updateFromFavoritesHandler} style={{ width: '100%' }} className={`login-button ${(!folderValue || !seriesValue || loadingFavoriteAdd) ? 'button-disabled' : ''} mt-3`}>
                            {loadingFavoriteAdd ? (
                                <Spinner animation="border" variant='light' as='span' style={{ width: '20px', height: '20px' }} />
                            ) : (
                                'Add Serie'
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default EditWidgetSeries
